import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import StrategiesPage from './pages/StrategiesPage';
import StrategyDetailPage from './pages/StrategyDetailPage';
import MarketPage from './pages/MarketPage';
import AIPage from './pages/AIPage';
import ProfilePage from './pages/ProfilePage';
import LinkBrokerPage from './pages/LinkBrokerPage';
import CopyDashboardPage from './pages/CopyDashboardPage';
import CopySettingsPage from './pages/CopySettingsPage';
import AccountPortfolioPage from './pages/AccountPortfolioPage';
import AdminPage from './pages/AdminPage';
import FundedAccountsPage from './pages/FundedAccountsPage';
import VerificationPage from './pages/VerificationPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import OnboardingPage from './pages/OnboardingPage';
import CheckoutPage from './pages/CheckoutPage';
import BottomTabBar from './components/BottomTabBar';
import IOSPrompt from './components/IOSPrompt';

function ProtectedRoute({ children }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--pp-bg)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--pp-primary)', borderTopColor: 'transparent' }} />
          <p className="text-xs tracking-widest" style={{ color: 'var(--pp-text-3)' }}>LOADING...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" state={{ from: location }} replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

const TABS = ['/dashboard', '/strategies', '/market', '/ai', '/profile'];

function AppLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const showTabBar = user && TABS.some(tab => location.pathname.startsWith(tab));

  return (
    <div className="App" style={{ background: 'var(--pp-bg)', minHeight: '100vh' }}>
      <Routes>
        <Route path="/" element={<AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/strategies" element={<ProtectedRoute><StrategiesPage /></ProtectedRoute>} />
        <Route path="/strategy/:id" element={<ProtectedRoute><StrategyDetailPage /></ProtectedRoute>} />
        <Route path="/market" element={<ProtectedRoute><MarketPage /></ProtectedRoute>} />
        <Route path="/ai" element={<ProtectedRoute><AIPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/link-broker" element={<ProtectedRoute><LinkBrokerPage /></ProtectedRoute>} />
        <Route path="/copy-dashboard" element={<ProtectedRoute><CopyDashboardPage /></ProtectedRoute>} />
        <Route path="/copy-settings/:subscriptionId" element={<ProtectedRoute><CopySettingsPage /></ProtectedRoute>} />
        <Route path="/account-portfolio" element={<ProtectedRoute><AccountPortfolioPage /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="/funded-accounts" element={<ProtectedRoute><FundedAccountsPage /></ProtectedRoute>} />
        <Route path="/verification" element={<ProtectedRoute><VerificationPage /></ProtectedRoute>} />
        <Route path="/notification-preferences" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showTabBar && <BottomTabBar />}
      <IOSPrompt />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppLayout />
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: 'var(--pp-overlay)',
              border: '1px solid var(--pp-border)',
              color: 'var(--pp-text)',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
